import React, { useEffect, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import useSWR from 'swr';

interface DataType {
    key: React.Key;
    id: number;
    itemCode: string;
    itemName: string;
    qty: number;
    location: string;
    status?: string;
    availableQty?: number;
}

interface ListItemWorkOrderProps {
    workOrderNo: string | null;
    existingItemCodes: string[];
    warehouse: string | null;
    onAddItems: (items: DataType[]) => void;
}

type DataIndex = keyof DataType;

const fetcher = async (url: string) => {
    const res = await axios.get(url);
    return res.data;
};

const ListItemWorkOrder: React.FC<ListItemWorkOrderProps> = ({
    workOrderNo,
    existingItemCodes,
    warehouse,
    onAddItems
}) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<DataType[]>([]);

    const { data: workOrderItemData, error: swrError, isLoading: swrLoading } = useSWR(
        workOrderNo && warehouse
            ? `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/work-orders/${workOrderNo}/available-items?existingItemCodes=${JSON.stringify(existingItemCodes)}&warehouse=${warehouse}`
            : null,
        fetcher
    );

    useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [workOrderNo, existingItemCodes, warehouse]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            if (typeof recordValue === 'number') {
                return recordValue.toString().toLowerCase().includes((value as string).toLowerCase());
            }
            return (recordValue as string)
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'itemCode',
            key: 'itemCode',
            width: '20%',
            ...getColumnSearchProps('itemCode'),
            sorter: (a, b) => a.itemCode.localeCompare(b.itemCode),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Item Name',
            dataIndex: 'itemName',
            key: 'itemName',
            width: '30%',
            ...getColumnSearchProps('itemName'),
            sorter: (a, b) => a.itemName.localeCompare(b.itemName),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            key: 'qty',
            width: '10%',
            ...getColumnSearchProps('qty'),
            sorter: (a, b) => a.qty - b.qty,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            width: '20%',
            ...getColumnSearchProps('location'),
            sorter: (a, b) => a.location.localeCompare(b.location),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '20%',
            ...getColumnSearchProps('status'),
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
            sortDirections: ['ascend', 'descend'],
            render: (text: string) => (
                <span style={{ color: text === 'Not Ready' ? 'red' : 'inherit' }}> { }
                    {text} { }
                </span>
            ),
        },
    ];

    const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: DataType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: DataType) => ({
            disabled: false,
            name: record.itemCode,
        })
    };


    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    return (
        <div id="i89y" style={transformStyle}>
            <div className="gjs-grid-row" id="iic4">
                <div className="gjs-grid-column" id="i1w5">
                    <Table<DataType>
                        columns={columns}
                        dataSource={workOrderItemData?.map((item: DataType) => ({
                            ...item,
                            key: item.id,
                        })) || []}
                        pagination={false}
                        loading={swrLoading}
                        rowSelection={rowSelection}
                    />
                    <Button
                        type="primary"
                        onClick={() => onAddItems(selectedRows)}
                        disabled={selectedRowKeys.length === 0}
                        style={{ marginTop: 16 }}
                    >
                        Add Selected Items
                    </Button>
                </div>
            </div>
        </div>
    );
};
export default ListItemWorkOrder;